import React, { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { Atmosphere } from '@/components/shared/Atmosphere'
import { Attribution } from '@/components/shared/Attribution'
import { DNA_PROFILES } from '@/data/destinations'
import { generateItinerary } from '@/services/ai'
import { fetchImages } from '@/services/images'
import { openDeal, getNetworkLabel, NETWORK_COLORS } from '@/services/affiliates'
import type { Destination, Deal, Img, Stop } from '@/types'

const DAY_OPTIONS = [2, 3, 4, 5, 6]

interface AiDay {
  day: number
  title: string
  highlight: string
}

interface DayPlan {
  day: number
  title: string
  highlight: string
  stops: Stop[]
}

// ─── Curated fallback: reshape the destination's real stops into N days ──────
function buildCuratedDays(dest: Destination, days: number): DayPlan[] {
  const stops = dest.stops.length > 0 ? dest.stops : []
  return Array.from({ length: days }, (_, i) => {
    const stop = stops.length > 0 ? stops[i % stops.length] : undefined
    const cleanName = stop?.name?.replace(/\s*[\p{Emoji}\u200d\uFE0F]+\s*$/gu, '').trim()
    return {
      day: i + 1,
      title: cleanName || `${dest.name} adventure`,
      highlight:
        stop?.description ||
        `Explore ${dest.name} together — a full day of family-friendly discovery and fun.`,
      stops: stop ? [stop] : [],
    }
  })
}

// ─── Merge AI day titles/highlights with curated stop imagery ────────────────
function mergeAiWithStops(dest: Destination, aiDays: AiDay[]): DayPlan[] {
  const stops = dest.stops.length > 0 ? dest.stops : []
  return aiDays
    .sort((a, b) => a.day - b.day)
    .map((d, i) => {
      const stop = stops.length > 0 ? stops[i % stops.length] : undefined
      return {
        day: d.day,
        title: d.title || stop?.name || `${dest.name} adventure`,
        highlight: d.highlight || stop?.description || `A day exploring ${dest.name} with the family.`,
        stops: stop ? [stop] : [],
      }
    })
}

function DayPhoto({ img, dest }: { img: Img | undefined; dest: Destination }) {
  const [failed, setFailed] = useState(false)
  const showPhoto = !!img?.url && !failed

  return (
    <div
      className="relative overflow-hidden rounded-xl flex-shrink-0 w-full sm:w-32 h-28 sm:h-full"
      style={{ background: dest.gradient }}
    >
      {showPhoto ? (
        <img
          src={img!.url}
          alt={img!.alt || dest.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-3xl">{dest.emoji}</div>
      )}
      {showPhoto && (
        <div className="absolute bottom-1 right-1">
          <Attribution image={img} className="text-[8px] bg-black/40 rounded px-1" />
        </div>
      )}
    </div>
  )
}

export function FullItineraryScreen() {
  const { selectedDestination, dnaKey, setScreen } = useAppStore()
  const dest = selectedDestination
  const profile = DNA_PROFILES[dnaKey]

  const defaultDays = useMemo(() => {
    if (!dest) return 3
    const n = dest.stops.length || 3
    return Math.min(6, Math.max(2, n))
  }, [dest])

  const [days, setDays] = useState<number>(defaultDays)
  const [aiDays, setAiDays] = useState<AiDay[] | null>(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiFailed, setAiFailed] = useState(false)
  const [narrative, setNarrative] = useState<string>('')
  const [dayImages, setDayImages] = useState<Record<number, Img>>({})

  useEffect(() => {
    setDays(defaultDays)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dest?.id])

  // ── Generate the AI day plan (silent curated fallback — never blank) ───────
  useEffect(() => {
    if (!dest || !profile) return
    let cancelled = false
    setAiLoading(true)
    setAiFailed(false)

    generateItinerary(dest.name, profile.name, days, dest.ageRange)
      .then((result) => {
        if (cancelled) return
        if (Array.isArray(result) && result.length > 0) {
          setAiDays(result)
          setAiFailed(false)
        } else {
          setAiDays(null)
          setAiFailed(true)
        }
        setAiLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setAiDays(null)
        setAiFailed(true)
        setAiLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dest?.id, profile?.name, days])

  // ── Narrative intro — curated sentence built from destination description ─
  useEffect(() => {
    if (!dest) return
    setNarrative(
      `Get ready for ${days} unforgettable days in ${dest.name}. ${dest.description} Every stop below is paced for happy kids and relaxed parents, with real partner savings built in along the way.`
    )
  }, [dest?.id, days])

  // ── Day plan: AI when available, else curated from real stops ─────────────
  const dayPlan: DayPlan[] = useMemo(() => {
    if (!dest) return []
    if (aiDays && aiDays.length > 0) return mergeAiWithStops(dest, aiDays)
    return buildCuratedDays(dest, days)
  }, [dest, aiDays, days])

  // ── Fetch a photo per day (graceful fallback to gradient) ──────────────────
  useEffect(() => {
    if (!dest) return
    let cancelled = false
    ;(async () => {
      const entries = await Promise.all(
        dayPlan.map(async (d) => {
          const query = d.stops[0]?.name
            ? `${d.stops[0].name.replace(/\s*[\p{Emoji}\u200d\uFE0F]+\s*$/gu, '')} ${dest.name}`
            : `${dest.name} family travel`
          const imgs = await fetchImages(query, 1)
          return [d.day, imgs[0]] as const
        })
      )
      if (cancelled) return
      const map: Record<number, Img> = {}
      entries.forEach(([day, img]) => {
        if (img) map[day] = img
      })
      setDayImages(map)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dest?.id, dayPlan.length])

  if (!dest || !profile) {
    setScreen('journey')
    return null
  }

  const aiActive = !aiLoading && !aiFailed && !!aiDays

  return (
    <div className="yonder-world min-h-screen flex flex-col">
      <Atmosphere density="low" />
      <Topbar
        centerContent={<span className="text-white/70 font-bold">{dest.name} · full itinerary</span>}
        backTo="card"
        backLabel="← Journey Card"
      />

      <div className="relative z-10 flex-1 px-4 py-6">
        <div className="max-w-3xl mx-auto">
          {/* Country › Destination hierarchy label */}
          <div className="flex items-center gap-1.5 text-[10px] font-black tracking-widest text-white/60 uppercase mb-2">
            <span>{dest.flag}</span>
            <span>{dest.country ?? dest.name}</span>
            <span className="text-white/30">›</span>
            <span className="text-sun-pale">{dest.name}</span>
          </div>

          {/* Header */}
          <motion.h1
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-black text-white text-2xl sm:text-3xl leading-tight mb-3"
            style={{ textShadow: '0 4px 30px rgba(255,149,0,0.3)' }}
          >
            Your perfect <span className="text-sun-pale italic">{days} days</span> in {dest.name}
          </motion.h1>

          {/* Narrative intro */}
          <div className="mb-2 min-h-[44px]">
            {aiLoading ? (
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-sun-pale"
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                    />
                  ))}
                </div>
                <span className="text-[12px] text-white/60 font-semibold italic">
                  Yonder AI is planning your family's days…
                </span>
              </div>
            ) : (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-[13px] font-semibold text-white/75 leading-relaxed max-w-2xl"
              >
                {narrative}
              </motion.p>
            )}
          </div>

          {/* AI attribution pill */}
          <div className="mb-4 inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-2.5 py-1">
            <span className="text-[9px] font-black tracking-wide text-white/70 uppercase">
              {aiActive ? '✦ Personalised by Claude AI' : 'CACHED · curated plan'}
            </span>
          </div>

          {/* Day-count selector */}
          <div className="flex flex-wrap gap-2 mb-6">
            {DAY_OPTIONS.map((n) => (
              <button
                key={n}
                onClick={() => setDays(n)}
                className={`px-4 py-2 rounded-full text-[12px] font-black transition-all border-2 ${
                  days === n
                    ? 'border-sun bg-gradient-to-r from-sun to-sun-light text-white shadow-glow-sun'
                    : 'border-white/15 bg-white/5 text-white/70 hover:border-sun-light/60 hover:bg-white/10'
                }`}
              >
                {n} days
              </button>
            ))}
          </div>

          {/* Day-by-day plan */}
          <div className="flex flex-col gap-4 mb-8">
            <AnimatePresence mode="popLayout">
              {dayPlan.map((d) => {
                const stop = d.stops[0]
                const img = dayImages[d.day]
                return (
                  <motion.div
                    key={d.day}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="rounded-3xl overflow-hidden yonder-cream-card shadow-cream-card"
                  >
                    <div className="flex flex-col sm:flex-row gap-3 p-3">
                      <DayPhoto img={img} dest={dest} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[9px] font-black tracking-widest text-sun uppercase mb-0.5">
                          Day {d.day}
                        </div>
                        <div className="text-[15px] font-black text-ink leading-snug mb-1">{d.title}</div>
                        <div className="text-[11px] font-semibold text-ink-2 leading-snug mb-2">
                          {d.highlight}
                        </div>

                        {stop && (() => {
                          const cleanStopName = stop.name.replace(/\s*[\p{Emoji}\u200d\uFE0F]+\s*$/gu, '').trim()
                          const isDuplicate = d.title.trim() === cleanStopName || d.highlight.trim() === stop.description.trim()
                          return (
                            <div className="border-t border-[#F0EBE3] pt-2 mt-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#F0EBE3] text-ink-3">
                                  ⏱ {stop.time}
                                </span>
                                <span className="text-[10px] font-black text-ink">{stop.name}</span>
                              </div>
                              {!isDuplicate && (
                                <div className="text-[10px] font-semibold text-ink-3 mt-1 leading-snug">
                                  {stop.description}
                                </div>
                              )}
                              {stop.familyTag && (
                                <div className="inline-block mt-1.5 text-[9px] font-bold px-2 py-0.5 rounded-full bg-sun-pale/30 text-sun">
                                  {stop.familyTag}
                                </div>
                              )}
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </div>

          {/* Partner offers for this trip */}
          {dest.deals.length > 0 && (
            <div className="mb-8">
              <div className="text-[11px] font-black tracking-widest text-sun-pale/90 uppercase mb-3">
                🎁 Partner offers for this trip
              </div>
              <div className="flex flex-col gap-2">
                {dest.deals.map((deal: Deal, i: number) => (
                  <button
                    key={i}
                    onClick={() => openDeal(deal, dest.name, profile.name)}
                    className="flex items-center gap-3 bg-white border-2 border-[#F0EBE3] rounded-2xl px-3 py-2.5 shadow-sm hover:border-sun hover:shadow-sun/15 hover:shadow-md active:scale-[0.98] transition-all text-left w-full group"
                    title={`Book via ${getNetworkLabel(deal.network)}`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-sand flex items-center justify-center text-lg flex-shrink-0">
                      {deal.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-black text-ink group-hover:text-sun transition-colors">
                        {deal.title}
                      </div>
                      <div className="text-[10px] font-semibold text-ink-3 mt-0.5">{deal.detail}</div>
                      <div
                        className="inline-block text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded mt-1"
                        style={{ background: NETWORK_COLORS[deal.network] + '18', color: NETWORK_COLORS[deal.network] }}
                      >
                        via {getNetworkLabel(deal.network)}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <div className="bg-sun-pale border-2 border-sun-light rounded-full text-[9px] font-black px-2.5 py-1 text-ink whitespace-nowrap">
                        {deal.saving}
                      </div>
                      <div className="text-[9px] font-black text-sun opacity-0 group-hover:opacity-100 transition-opacity">
                        Book now →
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Bottom CTA */}
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <button
              onClick={() => setScreen('card')}
              className="flex-1 bg-gradient-to-r from-sun to-sun-light text-white font-black text-[13px] py-3 rounded-2xl shadow-glow-sun"
            >
              ← Back to Journey Card
            </button>
            <button
              onClick={() => setScreen('card')}
              className="flex-1 bg-white/10 text-white/80 font-bold text-[12px] py-3 rounded-2xl hover:bg-white/15 transition-colors"
            >
              📱 Send to Phone
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
